import * as React from 'react';
import {
  RadioGroup as RACRadioGroup,
  Radio as RACRadio,
  type RadioGroupProps as RACRadioGroupProps,
  type RadioProps as RACRadioProps,
  type RadioRenderProps,
} from 'react-aria-components';

import { cn } from '../lib/cn';

/**
 * Klaret RadioGroup / RadioGroupItem (handoff §10.3).
 * Radio: 16×16 circle. Selected = accent ring + 6px inner dot (not filled disc).
 * Focus = global 2px ink outline (globals.css) — no accent ring.
 */
export const RadioGroup = React.forwardRef<HTMLDivElement, RACRadioGroupProps>(
  ({ className, ...props }, ref) => (
    <RACRadioGroup
      ref={ref}
      className={cn('grid gap-2', className)}
      {...props}
    />
  ),
);
RadioGroup.displayName = 'RadioGroup';

export interface RadioGroupItemProps extends Omit<RACRadioProps, 'children'> {
  className?: string;
  children?: React.ReactNode;
}

export const RadioGroupItem = React.forwardRef<HTMLLabelElement, RadioGroupItemProps>(
  ({ className, children, ...props }, ref) => (
    <RACRadio
      ref={ref}
      className={cn('group inline-flex cursor-pointer items-center gap-2', className)}
      {...props}
    >
      {({ isSelected, isDisabled }: RadioRenderProps) => (
        <>
          {/* circle control */}
          <span
            className={cn(
              'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
              'transition-colors duration-[var(--tvl-d-fast)]',
              isSelected
                ? 'border-[var(--tvl-accent)] bg-[var(--tvl-raised)]'
                : 'border-[var(--tvl-border-input)] bg-[var(--tvl-raised)]',
              !isSelected && 'group-hover:bg-[var(--tvl-wash-hover)]',
              isDisabled && 'cursor-not-allowed opacity-50',
            )}
          >
            {/* 6px inner dot — accent ring variant per §10.3 */}
            {isSelected ? (
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--tvl-accent)]" />
            ) : null}
          </span>
          {/* label */}
          {children ? (
            <span
              className={cn(
                'text-sm leading-5 text-[var(--tvl-ink)]',
                isDisabled && 'text-[var(--tvl-ink-disabled)]',
              )}
            >
              {children}
            </span>
          ) : null}
        </>
      )}
    </RACRadio>
  ),
);
RadioGroupItem.displayName = 'RadioGroupItem';
